import { useQuery } from '@tanstack/react-query'
import { UserService } from '@duck/api-definitions'
import { apis } from '@/modules/apiClients'
import { useAuth } from './useAuth'

export const personsQueryKeys = {
  all: () => ['persons'] as const,
  lists: () => ['persons', 'list'] as const,
  list: (personIds?: string[]) => ['persons', 'list', personIds] as const,
  details: () => ['persons', 'detail'] as const,
  detail: (personId: string) => ['persons', 'detail', personId] as const,
}

const fetchPersonsByIds = async (personIds: string[]): Promise<UserService.Person[]> => {
  if (!personIds || personIds.length === 0) {
    return []
  }

  try {
    // Filter server-side by the requested ids instead of pulling every person and filtering here
    // (admins are not org-scoped, so an unfiltered call would load the entire person table).
    const response = await apis.userService.personsGet(personIds)
    return response.data
  } catch (error) {
    console.error('Failed to fetch persons by IDs:', error)
    return []
  }
}

export const usePersons = (personIds?: string[]) => {
  const { isPartner, isAdmin } = useAuth()
  const enabled = (isPartner || isAdmin) && !!personIds && personIds.length > 0
  const query = useQuery({
    queryKey: personsQueryKeys.list(personIds),
    queryFn: () => {
      if (!personIds || personIds.length === 0) {
        return Promise.resolve([])
      }
      return fetchPersonsByIds(personIds)
    },
    enabled,
    staleTime: 30 * 60 * 1000, // 30 minutes
  })


  return {
    persons: query.data ?? [],
    isLoading: query.isPending && enabled,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  } as const
}
